// Facts the user tells us. Kept separate from anything the app infers (see learn.ts).

import type { Cuisine, Equipment } from './recipeMeta';

export type Goal =
  | 'maintain' | 'gain' | 'lose' | 'muscle' | 'performance'
  | 'quality' | 'balanced' | 'pattern' | 'none';

export const GOALS: { id: Goal; label: string; detail: string }[] = [
  { id: 'balanced', label: 'Eat more balanced meals', detail: 'Protein, vegetables and a grain on most plates' },
  { id: 'quality', label: 'Improve my overall diet', detail: 'More variety and more vegetables, at a steady pace' },
  { id: 'maintain', label: 'Maintain my weight', detail: 'Steady, satisfying meals' },
  { id: 'muscle', label: 'Build muscle', detail: 'Meals built around a protein source' },
  { id: 'performance', label: 'Support strength or sport', detail: 'Enough protein and energy around training' },
  { id: 'lose', label: 'Lose weight', detail: 'Balanced, filling meals. No calorie targets.' },
  { id: 'gain', label: 'Gain weight', detail: 'Heartier meals with protein and grains' },
  { id: 'pattern', label: 'Follow a dietary pattern', detail: 'Set it under allergies and diets' },
  { id: 'none', label: 'No specific goal', detail: 'Just help me cook' },
];

/** Pairs that can't be selected together. */
const CONFLICTS: [Goal, Goal][] = [['lose', 'gain'], ['lose', 'maintain'], ['gain', 'maintain']];

/** Toggle a goal, dropping anything it conflicts with. 'none' is exclusive. */
export function toggleGoal(current: Goal[], g: Goal): Goal[] {
  if (current.includes(g)) return current.filter((x) => x !== g);
  if (g === 'none') return ['none'];
  const blocked = new Set(CONFLICTS.flatMap(([a, b]) => (a === g ? [b] : b === g ? [a] : [])));
  return [...current.filter((x) => x !== 'none' && !blocked.has(x)), g];
}

/** Goals that make protein-per-kg context relevant (and so justify asking for weight). */
export const PROTEIN_GOALS: Goal[] = ['muscle', 'performance', 'gain'];

export type Heat = 'none' | 'mild' | 'hot';
export type Skill = 'new' | 'comfortable' | 'confident';
export type Shopping = 'weekly' | 'twice_weekly' | 'as_needed';
export type Budget = 'tight' | 'moderate' | 'flexible';
export type Activity = 'light' | 'moderate' | 'active';
export type Exercise = 'strength' | 'cardio' | 'sport' | 'walking' | 'yoga';
export type Priority = 'convenience' | 'variety' | 'nutrition' | 'affordability' | 'less_waste' | 'fitness';
export type MealSlot = 'dinner' | 'breakfast';

export const HEAT_LABELS: Record<Heat, string> = { none: 'No heat', mild: 'A little', hot: 'Bring it on' };
export const SKILL_LABELS: Record<Skill, string> = { new: 'Just starting', comfortable: 'Comfortable', confident: 'Confident' };
export const SHOPPING_LABELS: Record<Shopping, string> = { weekly: 'Once a week', twice_weekly: 'Twice a week', as_needed: 'As needed' };
export const BUDGET_LABELS: Record<Budget, string> = { tight: 'Keep it low', moderate: 'Moderate', flexible: 'Flexible' };
export const ACTIVITY_LABELS: Record<Activity, string> = { light: 'Mostly sitting', moderate: 'On my feet a fair bit', active: 'Very active' };
export const EXERCISE_LABELS: Record<Exercise, string> = { strength: 'Strength', cardio: 'Running or cardio', sport: 'Team sport', walking: 'Walking', yoga: 'Yoga or pilates' };
export const PRIORITY_LABELS: Record<Priority, { label: string; detail: string }> = {
  affordability: { label: 'Save money', detail: 'Fewer, cheaper extras to buy' },
  convenience: { label: 'Cook faster', detail: 'Quick, low-effort dinners' },
  less_waste: { label: 'Use what I have', detail: 'Use up your kitchen first' },
  nutrition: { label: 'Eat more balanced meals', detail: 'Protein, vegetables and a grain' },
  variety: { label: 'Discover new meals', detail: 'Something different most nights' },
  fitness: { label: 'Support a fitness or nutrition goal', detail: 'Pick a goal below' },
};
export const EQUIPMENT_LABELS: Record<Equipment, string> = { stovetop: 'Stovetop', oven: 'Oven', blender: 'Blender', toaster: 'Toaster' };
/** Minutes. 25 and 40 are kept so earlier saves stay valid; onboarding offers 15, 30, 60 and 120. */
export const TIME_CHOICES = [15, 25, 30, 40, 60, 120] as const;
export const TIME_OPTIONS: { value: TimeChoice; label: string; short: string }[] = [
  { value: 15, label: 'Under 15 min', short: 'Under 15' }, { value: 30, label: '15 to 30 min', short: '15 to 30' },
  { value: 60, label: '30 to 60 min', short: '30 to 60' }, { value: 120, label: 'Flexible', short: 'Flexible' },
];

export type Frustration = 'what_to_cook' | 'waste' | 'cost' | 'time' | 'goals' | 'boredom';
export const FRUSTRATIONS: { id: Frustration; label: string }[] = [
  { id: 'what_to_cook', label: 'I never know what to cook' },
  { id: 'waste', label: 'I waste food and ingredients' },
  { id: 'cost', label: 'Groceries cost too much' },
  { id: 'time', label: 'I don’t have enough time to cook' },
  { id: 'goals', label: 'I struggle to eat for my goals' },
  { id: 'boredom', label: 'I get bored of the same meals' },
];
/** What each frustration asks of the ranking, as the priority it implies. */
export const FRUSTRATION_PRIORITY: Partial<Record<Frustration, Priority>> = {
  waste: 'less_waste', cost: 'affordability', time: 'convenience', goals: 'nutrition', boredom: 'variety',
};

export type FoodLike =
  | 'meat' | 'rice' | 'pasta_noodles' | 'seafood' | 'vegetarian' | 'african_caribbean'
  | 'asian' | 'mediterranean' | 'comfort' | 'international' | 'unsure';
export const FOOD_LIKES: { id: FoodLike; label: string }[] = [
  { id: 'meat', label: 'Chicken and meat' },
  { id: 'rice', label: 'Rice dishes' },
  { id: 'pasta_noodles', label: 'Pasta and noodles' },
  { id: 'seafood', label: 'Fish and seafood' },
  { id: 'vegetarian', label: 'Vegetarian' },
  { id: 'african_caribbean', label: 'African and Caribbean' },
  { id: 'asian', label: 'Asian' },
  { id: 'mediterranean', label: 'Mediterranean' },
  { id: 'comfort', label: 'Comfort food' },
  { id: 'international', label: 'Other international' },
  { id: 'unsure', label: 'Not sure yet' },
];
export type TimeChoice = (typeof TIME_CHOICES)[number];

export type { Cuisine, Equipment };
